import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { AlertTriangle } from "lucide-react";

/** A short two-note beep. Browsers only allow sound after the page has been touched, so a failure is silent. */
function beep() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    [0, 0.28].forEach((at, i) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.frequency.value = i === 0 ? 880 : 660; o.connect(g); g.connect(ctx.destination);
      g.gain.setValueAtTime(0.0001, ctx.currentTime + at); g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + at + 0.02); g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + at + 0.25);
      o.start(ctx.currentTime + at); o.stop(ctx.currentTime + at + 0.27);
    });
    setTimeout(() => ctx.close && ctx.close(), 1200);
  } catch { /* no sound is fine */ }
}

/** The tablet's staff-area alerts, as chosen in Settings → Notifications. Polls quietly: when the tablet is offline the last answer stays. Plays a sound for one that is new since the last look (never for the first load). */
export function useKioskAlerts(api, active, every = 30000) {
  const [data, setData] = useState({ alerts: [], sound: false, ratioOn: true });
  const seen = useRef(null);
  useEffect(() => {
    if (!active) { seen.current = null; return undefined; }
    let alive = true;
    const pull = () => axios.get(`${api}/kiosk/alerts`, { silent: true }).then((r) => {
      if (!alive) return;
      const ids = new Set(r.data.alerts.map((a) => a.id));
      if (seen.current && r.data.sound && [...ids].some((id) => !seen.current.has(id))) beep();
      seen.current = ids; setData(r.data);
    }).catch(() => {});
    pull();
    const t = setInterval(pull, every);
    return () => { alive = false; clearInterval(t); };
  }, [api, active, every]);
  return data;
}

/** Red banner lines for incidents and children not collected (the ratio has its own banner beside it). */
export default function KioskAlerts({ data }) {
  const shown = (data?.alerts || []).filter((a) => a.kind !== "ratio");
  if (!shown.length) return null;
  return (
    <div role="alert" data-testid="kiosk-alerts" className="space-y-1.5 rounded-xl border border-[var(--hp-loss)] px-4 py-3 text-sm" style={{ background: "color-mix(in srgb, var(--hp-loss) 12%, transparent)" }}>
      {shown.map((a) => <p key={a.id} data-testid={`kiosk-alert-${a.kind}`} className="flex items-start gap-2 font-semibold text-[var(--hp-loss)]"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /><span className="text-[var(--hp-heading)]">{a.text}</span></p>)}
    </div>
  );
}
